import type { CalculationResult } from '../types';
import { AssumptionTag, Disclosure } from './Primitives';
import { formatDate, formatExact, formatNumber } from '../utils/format';
import { DISCLAIMER_SHORT } from '../data/provenance';

export function ResultPanel({ result }: { result: CalculationResult | null }) {
  if (!result) {
    return (
      <div className="panel result-panel result-empty">
        <h3 className="panel-title">Result</h3>
        <p className="empty-state">
          Enter a scenario on the left. The result, the derivation behind it, and the sensitivity of that result to your
          Q10 assumption all appear as soon as the arithmetic is defined.
        </p>
      </div>
    );
  }

  return (
    <div className="panel result-panel">
      <h3 className="panel-title">Result</h3>

      <div className="result-block">
        <p className="result-label">Accelerated aging factor</p>
        <p className="result-figure">
          <span className="result-symbol">AAF =</span> {formatNumber(result.aaf, 4)}
        </p>
        <p className="result-exact">exact: {formatExact(result.aaf)}</p>
      </div>

      <div className="result-block">
        <p className="result-label">Accelerated aging duration</p>
        <p className="result-figure">
          {formatNumber(result.aatDays, 3)} <span className="result-unit">days</span>
        </p>
        <p className="result-exact">exact: {formatExact(result.aatDays)} days</p>
      </div>

      <div className="result-divider">
        <span>mathematical result above · display conventions below</span>
      </div>

      <dl className="result-secondary">
        <div>
          <dt>Rounded up to whole days</dt>
          <dd>{Number.isFinite(result.aatDaysWholeCeiling) ? `${result.aatDaysWholeCeiling} days` : '—'}</dd>
        </div>
        {result.projectedCompletionDate && (
          <div>
            <dt>Projected completion</dt>
            <dd>{formatDate(result.projectedCompletionDate)}</dd>
          </div>
        )}
        <div>
          <dt>Q10 used</dt>
          <dd>
            <AssumptionTag>{result.inputs.q10}</AssumptionTag>
          </dd>
        </div>
      </dl>

      <p className="result-boundary">
        This is arithmetic, not a test plan. What duration you schedule, how you sample it, and whether this model
        represents your system remain engineering decisions.
      </p>

      <Disclosure summary="Scope of this result">
        <p>{DISCLAIMER_SHORT}</p>
      </Disclosure>
    </div>
  );
}
